import { Check, Copy, Loader2, Sparkles, Users } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { getAddress, zeroAddress } from "viem";
import type { UseReferralResult } from "../hooks/useReferral";
import { formatToken } from "../lib/format";
import { REFERRAL_COPY, REFERRAL_HOLD_HOURS, REFERRAL_MIN_BUY_LABEL, REFERRAL_REWARD_BPS } from "../lib/referral";

interface ReferralPanelProps {
  referral: UseReferralResult;
}

type ClaimState =
  | { kind: "idle" }
  | { kind: "pending" }
  | { kind: "success"; txHash?: string }
  | { kind: "error"; message: string };

function truncateAddress(value: string): string {
  if (value.length <= 12) return value;
  try {
    const checksum = getAddress(value);
    return `${checksum.slice(0, 6)}…${checksum.slice(-4)}`;
  } catch {
    return `${value.slice(0, 6)}…${value.slice(-4)}`;
  }
}

function safeBigInt(value: string | undefined): bigint {
  if (!value) return 0n;
  try {
    return BigInt(value);
  } catch {
    return 0n;
  }
}

export function ReferralPanel({ referral }: ReferralPanelProps) {
  const [copied, setCopied] = useState(false);
  const [claimState, setClaimState] = useState<ClaimState>({ kind: "idle" });

  const claimableAmount = safeBigInt(referral.stats?.claimableAmount);
  const claimableIds = useMemo(() => referral.claimable.map((c) => c.id), [referral.claimable]);
  const rewardPct = `${REFERRAL_REWARD_BPS / 100}%`;

  const handleCopy = useCallback(async () => {
    if (!referral.inviteLink) return;
    try {
      await navigator.clipboard.writeText(referral.inviteLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // best-effort: ignore copy failures
    }
  }, [referral.inviteLink]);

  const handleClaim = useCallback(async () => {
    if (claimableIds.length === 0) return;
    setClaimState({ kind: "pending" });
    try {
      const response = await referral.claim(claimableIds);
      if (response.ok) {
        setClaimState({ kind: "success", txHash: response.txHash });
      } else {
        setClaimState({ kind: "error", message: response.error ?? "领取失败" });
      }
    } catch (cause) {
      setClaimState({
        kind: "error",
        message: cause instanceof Error ? cause.message : "领取失败",
      });
    }
  }, [claimableIds, referral]);

  if (!referral.enabled) return null;

  const connected = referral.isConnected && Boolean(referral.address);
  const claimDisabled = !connected || claimableAmount === 0n || claimState.kind === "pending";

  const stats = referral.stats;

  return (
    <section className="glass p-5 lg:p-6 lg:col-span-2 relative overflow-hidden">
      {/* Wing aura — keeps the panel visually anchored to the referral color */}
      <div
        className="pointer-events-none absolute -top-20 -right-16 w-64 h-64 rounded-full bg-wing/10 blur-3xl"
        aria-hidden
      />

      <header className="flex items-center gap-2.5 mb-2 relative">
        <Users className="w-5 h-5 text-wing" strokeWidth={1.75} aria-hidden />
        <h3 className="text-sm font-mono uppercase tracking-wider text-ink">邀请奖励</h3>
        <span className="ml-auto inline-flex items-center gap-1.5 rounded-chip border border-wing/30 bg-wing/10 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-wing">
          <Sparkles className="w-3 h-3" strokeWidth={2} aria-hidden />
          {rewardPct} 奖励
        </span>
      </header>
      <p className="text-xs text-ink-dim leading-relaxed mb-5 max-w-2xl">
        邀请朋友通过官方页面买入蜂鸟。单笔买入不少于{" "}
        <span className="font-mono text-ink">{REFERRAL_MIN_BUY_LABEL}</span> 且持币{" "}
        <span className="font-mono text-ink">{REFERRAL_HOLD_HOURS} 小时</span>后，{REFERRAL_COPY.inviterLabel}可领取{" "}
        <span className="font-mono text-wing">{rewardPct}</span> 奖励。{REFERRAL_COPY.notStakingLine}。
      </p>

      {!connected ? (
        <div className="rounded-card border border-dashed border-white/10 bg-white/[0.02] px-4 py-6 text-center">
          <p className="text-sm text-ink-dim">连接钱包查看你的邀请数据</p>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[1.2fr_1fr]">
          {/* Left: invite link + claim CTA */}
          <div className="space-y-5">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-dim mb-2">
                你的专属邀请链接
              </div>
              <div className="flex items-stretch gap-2">
                <input
                  type="text"
                  readOnly
                  value={referral.inviteLink}
                  aria-label="邀请链接"
                  className="flex-1 min-w-0 rounded-chip border border-white/10 bg-white/[0.02] px-3 py-2.5 text-xs font-mono text-ink-dim focus:outline-none focus:border-wing/40 focus:text-ink transition"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  aria-label="复制邀请链接"
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-chip border border-white/10 bg-white/[0.02] px-3 py-2.5 text-xs font-mono uppercase tracking-wider text-ink-dim hover:text-ink hover:border-wing/30 transition"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-leaf" strokeWidth={2} aria-hidden />
                      <span className="text-leaf">已复制</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" strokeWidth={1.75} aria-hidden />
                      复制
                    </>
                  )}
                </button>
              </div>
            </div>

            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-dim mb-2">
                可领取奖励
              </div>
              <div className="flex items-end justify-between gap-3 mb-3">
                <div className="text-2xl lg:text-3xl font-semibold font-mono tabular-nums text-wing leading-none">
                  {formatToken(claimableAmount, "蜂鸟")}
                </div>
                <div className="text-[10px] font-mono tabular-nums text-ink-dim pb-1">
                  {claimableIds.length} 笔
                </div>
              </div>
              <button
                type="button"
                onClick={() => void handleClaim()}
                disabled={claimDisabled}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
              >
                {claimState.kind === "pending" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} aria-hidden />
                    领取中…
                  </>
                ) : (
                  <>领取 {formatToken(claimableAmount, "蜂鸟")}</>
                )}
              </button>
              {claimState.kind === "success" ? (
                <p className="mt-2 text-xs text-leaf font-mono">已领取 · 等待链上确认</p>
              ) : null}
              {claimState.kind === "error" ? (
                <p className="mt-2 text-xs text-ember font-mono">{claimState.message}</p>
              ) : null}
            </div>
          </div>

          {/* Right: stats grid */}
          <div className="grid grid-cols-2 gap-3 content-start">
            <StatTile
              label="累计邀请"
              value={(stats?.totalInvited ?? 0).toString()}
              accent="text-ink"
            />
            <StatTile
              label="累计已领"
              value={formatToken(safeBigInt(stats?.totalRewarded), "蜂鸟")}
              accent="text-ink"
            />
            <StatTile
              label="待生效"
              value={formatToken(safeBigInt(stats?.pendingAmount), "蜂鸟")}
              accent="text-ink-dim"
            />
            <StatTile
              label="可领取"
              value={formatToken(claimableAmount, "蜂鸟")}
              accent="text-wing"
            />
          </div>
        </div>
      )}

      {connected && referral.history.length > 0 ? (
        <div className="mt-6 border-t border-white/[0.04] pt-4">
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-dim mb-3">
            最近领取
          </div>
          <ul className="space-y-2">
            {referral.history.slice(0, 5).map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-3 text-xs font-mono"
              >
                <span className="text-ink-dim tabular-nums">{truncateAddress(item.invitee)}</span>
                <span className="text-ink tabular-nums">
                  {formatToken(safeBigInt(item.rewardAmount), "蜂鸟")}
                </span>
                <span className="inline-flex items-center rounded-chip border border-leaf/30 bg-leaf/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-leaf">
                  已领
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {referral.error ? (
        <p className="mt-4 text-xs text-ember font-mono">{referral.error}</p>
      ) : null}
    </section>
  );
}

interface StatTileProps {
  label: string;
  value: string;
  accent: string;
}

function StatTile({ label, value, accent }: StatTileProps) {
  return (
    <div className="rounded-chip border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-dim mb-1.5">
        {label}
      </div>
      <div className={`text-sm font-mono tabular-nums break-all ${accent}`}>{value}</div>
    </div>
  );
}
